import {
  Alert,
  AlertIcon,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';
import InputNumber from 'components/InputNumber';
import SelectForm from 'components/SelectForm';
import FormLabelCustom from 'components/FormLabel';
import useAuth from 'contexts/useAuth';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { createWithdrawal, validatePixKey } from 'services/api.service';
import { validCnpj, validCpf } from 'services/validDocument';
import { maskCurrency } from 'utils/number';
import { onlyNumbers } from 'utils/string';
import { mask } from 'utils/mask';
import { useTwoFaHelper } from 'contexts/TwoFaCheckContext';
import CustomButton from 'components/CustomButton';
import FormWrapper from 'components/FormWrapper';
import ScheduledWithdrawalAlert from 'components/ScheduledWithdrawalAlert';
import { ConsultPixKey } from '../types/QRcode/qrcode.type';
import { queryClient } from 'index';

const applyPixKeyMask = (value: string, type: string): string => {
  const digits = onlyNumbers(value);
  switch (type) {
    case 'CPF':
      return mask(digits, '999.999.999-99');
    case 'CNPJ':
      return mask(digits, '99.999.999/9999-99');
    case 'PHONE':
      return mask(digits, digits.length > 10 ? '(99) 99999-9999' : '(99) 9999-9999');
    default:
      return value;
  }
};

const getPixKeyRaw = (value: string, type: string): string => {
  if (!value) return '';
  const digits = onlyNumbers(value);
  switch (type) {
    case 'CPF':
    case 'CNPJ':
      return digits;
    case 'PHONE':
      return `+55${digits}`;
    case 'EMAIL':
      return value.trim().toLowerCase();
    case 'EVP':
      return value.trim().toLowerCase();
    default:
      return value;
  }
};

const WithdrawPix: React.FC<{ onClose: () => void; asPage?: boolean }> = ({ onClose, asPage }) => {
  const toast = useToast();
  const { userData, updateUserData } = useAuth();
  const [step, setStep] = React.useState<number>(0);
  const [loading, setLoading] = React.useState(false);
  const [pixKey, setPixKey] = React.useState<ConsultPixKey | undefined>();
  const [isFavorite, setIsFavorite] = React.useState(true);

  const { mutateAsync: consultPixKey } = useMutation(['consultPixKey'], async (keyPix: string) =>
    validatePixKey(keyPix),
  );

  const {
    control,
    handleSubmit,
    register,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm<{
    pixKeyType: string;
    keyPix: string;
    amount: number;
    description: string;
  }>();

  const pixKeyType = watch('pixKeyType');

  const amountValue = Number(watch('amount') || 0);
  const realAvailable = +userData.balance - +userData.amountLocked;
  const accountLimit = +(userData.accountLimit || 0);
  const availableWithLimit = realAvailable + accountLimit;
  const usingLimit = accountLimit > 0 && amountValue > realAvailable;

  const { requestTwoFa } = useTwoFaHelper();

  const handleWithdrawPix = async (data: any) => {
    const code = await requestTwoFa();
    if (!code?.id) {
      return;
    }

    setLoading(true);
    const rawKey = getPixKeyRaw(data.keyPix, data.pixKeyType);

    try {
      await createWithdrawal(code.id, {
        ...data,
        type: 'DICT',
        keyPix: rawKey,
        endToEndId: pixKey?.endToEndId,
        integrationId: pixKey?.integrationId,
        accountHolderName: pixKey?.name,
        isFavorite,
      });
      updateUserData();
      toast({
        title: 'Sucesso',
        description: 'Saque criado com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      queryClient.refetchQueries({ queryKey: ['findAllWithdrawers'] });
      onClose();
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error?.response?.data?.message || 'Erro ao criar saque',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleValidatePixKey = async () => {
    const rawKey = getPixKeyRaw(getValues('keyPix'), getValues('pixKeyType'));
    setLoading(true);

    try {
      const res = await consultPixKey(rawKey);
      setStep(1);
      toast({
        title: 'Sucesso',
        description: 'Chave pix válida',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      setPixKey(res);
    } catch (err) {
      toast({
        title: 'Aviso',
        description: 'Não foi possível validar a chave pix, mas você pode continuar',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      setPixKey(undefined);
      setStep(1);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormWrapper
      title="Saque Pix"
      onClose={onClose}
      asPage={asPage}
      isLoading={loading}
      footer={
        <>
          {step === 1 && (
            <CustomButton
              colorScheme=""
              isLoading={loading}
              onClick={() => setStep(0)}
            >
              Voltar
            </CustomButton>
          )}
          <CustomButton
            colorScheme="green"
            isLoading={loading}
            onClick={step === 1 ? handleSubmit(handleWithdrawPix) : handleValidatePixKey}
          >
            {step === 1 ? 'Criar saque' : 'Validar chave pix'}
          </CustomButton>
        </>
      }
    >
      <ScheduledWithdrawalAlert />
      {step === 0 && (
        <>
          <SelectForm
            control={control}
            label="Tipo de chave Pix"
            name="pixKeyType"
            placeholder="Selecione o tipo"
            rules={{ required: 'Campo obrigatório' }}
            options={[
              { label: 'CPF', value: 'CPF' },
              { label: 'CNPJ', value: 'CNPJ' },
              { label: 'E-mail', value: 'EMAIL' },
              { label: 'Telefone', value: 'PHONE' },
              { label: 'Chave aleatória (EVP)', value: 'EVP' },
            ]}
            onChange={() => {
              setValue('keyPix', '');
            }}
          />

          {pixKeyType && (
            <Controller
              control={control}
              name="keyPix"
              rules={{
                required: 'Campo obrigatório',
                validate: {
                  valid: (value) => {
                    if (!value) return true;
                    const digits = onlyNumbers(value);
                    switch (pixKeyType) {
                      case 'CPF':
                        return (digits.length === 11 && validCpf(digits)) || 'CPF inválido';
                      case 'CNPJ':
                        return (digits.length === 14 && validCnpj(digits)) || 'CNPJ inválido';
                      case 'PHONE':
                        return (digits.length >= 10 && digits.length <= 11) || 'Telefone inválido';
                      case 'EMAIL':
                        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) || 'E-mail inválido';
                      case 'EVP':
                        return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value) || 'Chave aleatória inválida';
                      default:
                        return true;
                    }
                  },
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <FormControl isInvalid={!!error} w="full">
                  <FormLabelCustom label="Chave Pix" name="keyPix" props={{}} />
                  <Input
                    {...field}
                    w="full"
                    size="sm"
                    bg="white"
                    placeholder={
                      pixKeyType === 'CPF' ? '000.000.000-00'
                        : pixKeyType === 'CNPJ' ? '00.000.000/0000-00'
                        : pixKeyType === 'PHONE' ? '(00) 00000-0000'
                        : pixKeyType === 'EMAIL' ? 'email@exemplo.com'
                        : 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'
                    }
                    maxLength={
                      pixKeyType === 'CPF' ? 14
                        : pixKeyType === 'CNPJ' ? 18
                        : pixKeyType === 'PHONE' ? 15
                        : undefined
                    }
                    onChange={(e) => {
                      const val = e.target.value;
                      if (['CPF', 'CNPJ', 'PHONE'].includes(pixKeyType)) {
                        field.onChange(applyPixKeyMask(val, pixKeyType));
                      } else {
                        field.onChange(val);
                      }
                    }}
                  />
                  <FormErrorMessage>{error?.message}</FormErrorMessage>
                </FormControl>
              )}
            />
          )}
          <InputNumber
            control={control}
            percentage={false}
            placeholder="R$ 0,00"
            label={'Valor'}
            name={`amount`}
            rules={{
              required: 'Campo obrigatório',
              validate: {
                positive: (value) => value > 0 || 'O valor deve ser maior que 0',
                max: (value) =>
                  value <= availableWithLimit ||
                  (accountLimit > 0
                    ? 'O valor excede o saldo disponível somado ao limite de conta'
                    : 'O valor deve ser menor que o saldo disponível'),
              },
            }}
          />

          <VStack align="start" spacing={1} w="full">
            <Text>
              Saldo disponível: <b>{maskCurrency(realAvailable)}</b>
            </Text>
            {accountLimit > 0 && (
              <Text fontSize="sm" color="gray.600">
                Limite de conta: <b>{maskCurrency(accountLimit)}</b>
              </Text>
            )}
          </VStack>
          {usingLimit && (
            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              <Text fontSize="sm">
                {realAvailable < 0
                  ? `Seu saldo está negativo. Este saque utilizará o limite de conta (limite de ${maskCurrency(accountLimit)}).`
                  : `Este saque utilizará ${maskCurrency(
                      Math.min(amountValue - realAvailable, accountLimit),
                    )} do seu limite de conta.`}
              </Text>
            </Alert>
          )}
          <FormControl isInvalid={!!errors.description?.message}>
            <FormLabel
              display="flex"
              ms="4px"
              fontSize="sm"
              mb="8px"
            >
              Descrição<Text>*</Text>
            </FormLabel>
            <Input
              isRequired={true}
              variant="auth"
              fontSize="sm"
              size="sm"
              {...register('description', {
                maxLength: {
                  value: 100,
                  message: 'O limite de caracteres é de 100',
                },
              })}
              ms={{ base: '0px', md: '0px' }}
              type="text"
              rounded="sm"
              placeholder="Pagamento de contas"
            />
            <FormErrorMessage>{errors.description?.message}</FormErrorMessage>
          </FormControl>
        </>
      )}
      {step === 1 && (
        <>
          {!pixKey && (
            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              <Text fontSize="sm">
                Não foi possível validar a chave Pix. Verifique se a chave está correta antes de confirmar.
              </Text>
            </Alert>
          )}
          {pixKey && (
            <VStack
              w="full"
              p={4}
              bg="gray.50"
              borderRadius="md"
              border="1px"
              borderColor="gray.200"
              spacing={3}
              align="stretch"
            >
              <Text fontSize="lg" fontWeight="bold">
                Destinatário
              </Text>
              <HStack justify="space-between">
                <Text fontWeight="medium" color="gray.600">Nome</Text>
                <Text>{pixKey.name}</Text>
              </HStack>
              <HStack justify="space-between">
                <Text fontWeight="medium" color="gray.600">Chave Pix</Text>
                <Text>{pixKey.key}</Text>
              </HStack>
              <HStack justify="space-between">
                <Text fontWeight="medium" color="gray.600">Documento</Text>
                <Text>{pixKey.document}</Text>
              </HStack>
            </VStack>
          )}
          {pixKey && (
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              <Text fontSize="sm">
                Data da última busca da chave: {new Date(pixKey.lastModified).toLocaleString('pt-BR')}
              </Text>
            </Alert>
          )}
          <VStack w="full" spacing={2} align="center" py={4}>
            <Text color="gray.600">Valor solicitado</Text>
            <Text fontSize="2xl" fontWeight="bold" color="#2D9CDB">
              {maskCurrency(getValues('amount'))}
            </Text>
            <Text color="gray.600">
              Descrição: <b>{getValues('description')}</b>
            </Text>
          </VStack>
          <Checkbox
            isChecked={isFavorite}
            onChange={(e) => setIsFavorite(e.target.checked)}
            colorScheme="blue"
          >
            Salvar como favorecido
          </Checkbox>
        </>
      )}
    </FormWrapper>
  );
};

export default WithdrawPix;
